import { useEffect } from 'react';

/** Less than this at the bottom is a toolbar or a pinch zoom, not a keyboard. */
const MIN_KEYBOARD_PX = 80;

const typing = () => {
  const el = document.activeElement;
  return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
};

/**
 * Soft keyboard (M0-06, gaps A39): Android Chrome and iOS Safari only shrink the visual viewport, so a bottom
 * sheet's search field would sit under the keys. While typing, `--kb` holds the covered height; the shell
 * shrinks by it, every sheet moves up with it, and the field is scrolled into view.
 */
export function KeyboardInset() {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const root = document.documentElement;
    let last = -1;
    const update = () => {
      const covered = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      const inset = typing() && covered > MIN_KEYBOARD_PX ? covered : 0;
      if (inset === last) return;
      last = inset;
      root.style.setProperty('--kb', `${String(inset)}px`);
      if (inset > 0) (document.activeElement as HTMLElement).scrollIntoView({ block: 'nearest' });
    };
    const events = ['resize', 'scroll'] as const;
    for (const type of events) vv.addEventListener(type, update);
    window.addEventListener('focusin', update);
    window.addEventListener('focusout', update);
    update();
    return () => {
      for (const type of events) vv.removeEventListener(type, update);
      window.removeEventListener('focusin', update);
      window.removeEventListener('focusout', update);
      root.style.removeProperty('--kb');
    };
  }, []);
  return null;
}
